import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth/AuthProvider";
import type {
  Broadcast,
  BroadcastReceipt,
  BroadcastWithReceipt,
  ComposeBroadcastInput,
} from "./types";

const KEYS = {
  inbox: (uid: string) => ["broadcasts-inbox", uid],
  sent:  ["broadcasts-sent"] as const,
};

/** User-facing inbox: all broadcasts targeted to me. */
export const useMyBroadcasts = () => {
  const { user } = useAuth();
  return useQuery({
    queryKey: KEYS.inbox(user?.id ?? ""),
    enabled: !!user,
    queryFn: async (): Promise<BroadcastWithReceipt[]> => {
      const { data: receipts, error: rErr } = await supabase
        .from("broadcast_receipts")
        .select("*")
        .eq("user_id", user!.id)
        .order("created_at", { ascending: false })
        .limit(100);
      if (rErr) throw rErr;
      const ids = (receipts ?? []).map((r) => r.broadcast_id);
      if (ids.length === 0) return [];
      const { data: bcasts, error: bErr } = await supabase
        .from("broadcasts")
        .select("*")
        .in("id", ids);
      if (bErr) throw bErr;
      const byId = new Map<string, Broadcast>(
        (bcasts ?? []).map((b) => [b.id, b as Broadcast]),
      );
      return (receipts ?? [])
        .map((r): BroadcastWithReceipt | null => {
          const b = byId.get(r.broadcast_id);
          if (!b) return null;
          return { ...b, receipt: r as BroadcastReceipt };
        })
        .filter((x): x is BroadcastWithReceipt => x !== null);
    },
  });
};

export const useUnreadBroadcasts = () => {
  const q = useMyBroadcasts();
  return {
    ...q,
    unread: (q.data ?? []).filter(
      (b) => !b.receipt?.read_at && !b.receipt?.dismissed_at,
    ),
  };
};

export const useMarkBroadcastRead = () => {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (broadcastId: string) => {
      const { error } = await supabase
        .from("broadcast_receipts")
        .update({ read_at: new Date().toISOString() })
        .eq("broadcast_id", broadcastId)
        .eq("user_id", user!.id);
      if (error) throw error;
    },
    onSuccess: () => {
      if (user) qc.invalidateQueries({ queryKey: KEYS.inbox(user.id) });
    },
  });
};

export const useDismissBroadcast = () => {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (broadcastId: string) => {
      const { error } = await supabase
        .from("broadcast_receipts")
        .update({
          dismissed_at: new Date().toISOString(),
          read_at: new Date().toISOString(),
        })
        .eq("broadcast_id", broadcastId)
        .eq("user_id", user!.id);
      if (error) throw error;
    },
    onSuccess: () => {
      if (user) qc.invalidateQueries({ queryKey: KEYS.inbox(user.id) });
    },
  });
};

/** Founder/CS composer. */
export const useSendBroadcast = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ComposeBroadcastInput) => {
      const { data, error } = await supabase.functions.invoke("send-broadcast", {
        body: input,
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data as { broadcast_id: string; recipients: number; delivered: number; failed: number };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.sent });
    },
  });
};

/** Founder/CS history of broadcasts they've sent. */
export const useSentBroadcasts = () => {
  const { user } = useAuth();
  return useQuery({
    queryKey: KEYS.sent,
    enabled: !!user,
    queryFn: async (): Promise<Broadcast[]> => {
      const { data, error } = await supabase
        .from("broadcasts")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      return (data ?? []) as Broadcast[];
    },
  });
};
